/**
 * Sectioned, spec-driven editor for bot default config. Reused by the org
 * defaults panel (admin) and the personal defaults panel (trader), and the
 * spawn form's advanced popover. Deliberately NOT a flat grid: fields are
 * grouped by meaning per BOT_KIND_SPECS, and `orgOnly` (strategy) sections are
 * hidden from the personal scope. Blank inputs mean "inherit the next layer"
 * (personal → org → edge built-in), so every value is optional.
 */
import { useState } from 'react';
import { cn } from '@/lib/infra/utils';
import { BOT_KIND_SPECS, type BotField } from '@/lib/bots/configSpec';
import type { BotKindConfig } from '@/lib/bots/types';

type ByKind = Partial<Record<string, BotKindConfig>>;

/** Shallow-clone a byKind map so edits don't mutate the source (e.g. the store). */
export function cloneByKind(src: ByKind | undefined): ByKind {
  const out: ByKind = {};
  for (const [k, v] of Object.entries(src ?? {})) out[k] = { ...v };
  return out;
}

export function BotDefaultsEditor({
  byKind,
  scope,
  onChange,
}: {
  byKind: ByKind;
  scope: 'org' | 'personal';
  onChange: (next: ByKind) => void;
}) {
  const setField = (kind: string, key: string, raw: string) => {
    const next = cloneByKind(byKind);
    const cfg = (next[kind] ??= {});
    if (raw.trim() === '') {
      delete cfg[key]; // blank → inherit
    } else {
      const n = Number(raw);
      if (Number.isFinite(n)) cfg[key] = n;
    }
    if (Object.keys(cfg).length === 0) delete next[kind];
    onChange(next);
  };

  const [active, setActive] = useState(BOT_KIND_SPECS[0]?.kind ?? '');
  const spec = BOT_KIND_SPECS.find((k) => k.kind === active) ?? BOT_KIND_SPECS[0];
  const sections = spec.sections.filter((s) => scope === 'org' || !s.orgOnly);
  const cfg = byKind[spec.kind] ?? {};

  return (
    <div>
      {/* One tab per bot kind — keeps the card compact instead of stacking every
          kind's full config into one long scroll. */}
      <div className="border-border mb-3 flex flex-wrap gap-1 border-b">
        {BOT_KIND_SPECS.map((k) => (
          <button
            key={k.kind}
            type="button"
            onClick={() => setActive(k.kind)}
            className={cn(
              '-mb-px border-b-2 px-3 py-1.5 text-xs font-medium transition-colors',
              active === k.kind
                ? 'border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground border-transparent',
            )}
          >
            {k.label}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {sections.map((section) => (
          <div key={section.title}>
            <div className="text-muted-foreground mb-1 text-[10px] uppercase tracking-wider">
              {section.title}
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {section.fields.map((f) => (
                <FieldInput
                  key={f.key}
                  field={f}
                  value={cfg[f.key]}
                  onChange={(raw) => setField(spec.kind, f.key, raw)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="text-muted-foreground mt-3 text-[10px]">
        Blank inherits {scope === 'personal' ? 'the org default, then ' : ''}the engine built-in
        (shown as the placeholder). Traders can still override per spawn.
      </p>
    </div>
  );
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: BotField;
  value: number | undefined;
  onChange: (raw: string) => void;
}) {
  return (
    <label className="block" title={field.help}>
      <span className="text-muted-foreground flex items-center gap-1 text-[10px]">
        {field.label}
        {field.unit ? <span className="opacity-60">{field.unit}</span> : null}
      </span>
      <input
        type="number"
        inputMode="numeric"
        value={value ?? ''}
        min={field.nonNegative ? 0 : undefined}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.builtin !== undefined ? String(field.builtin) : '—'}
        className="bg-background border-border text-foreground focus:border-primary mt-0.5 w-full rounded border px-1.5 py-1 text-xs focus:outline-none"
      />
    </label>
  );
}
